// Danh tính ổn định của Blueprint (ADR-001 mục 2): tiền tố + ULID 26 ký tự (Crockford base32 của 128 bit). CSDL lưu cùng 128
// bit dạng `uuid`. Engine sinh 128 bit theo UUIDv7 (48 bit thời gian ms + ngẫu nhiên) ⇒ vừa là ULID sắp theo thời gian, vừa là
// uuid hợp lệ (version 7, variant RFC 4122) cho mọi cổng kiểm uuid hiện có. `tmp_*` chỉ dùng trong proposal cho thực thể mới.

export const ID_PREFIXES = { collection: "col", field: "fld", option: "opt", view: "viw", policy: "pol", scopeType: "sct", task: "tsk", flow: "flw", toolBinding: "tlb" } as const;
export type IdKind = keyof typeof ID_PREFIXES;
export type IdPrefix = (typeof ID_PREFIXES)[IdKind];

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ULID = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const TMP_ID = /^tmp_[a-z0-9_]{1,64}$/;

export function uuidToUlid(uuid: string): string {
  if (!UUID.test(uuid)) throw new TypeError(`không phải uuid: ${uuid}`);
  let n = BigInt(`0x${uuid.replace(/-/g, "")}`);
  let out = "";
  for (let i = 0; i < 26; i += 1) {
    out = CROCKFORD[Number(n & 31n)] + out;
    n >>= 5n;
  }
  return out;
}

export function ulidToUuid(ulid: string): string {
  if (!ULID.test(ulid)) throw new TypeError(`không phải ULID: ${ulid}`);
  let n = 0n;
  for (const c of ulid) n = (n << 5n) | BigInt(CROCKFORD.indexOf(c));
  const h = n.toString(16).padStart(32, "0");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** `fld_01JB…` ⇒ uuid; sai tiền tố / sai dạng ⇒ null (validator quy ra BLUEPRINT_REF_UNKNOWN). */
export function decodeId(kind: IdKind, id: string): string | null {
  const prefix = `${ID_PREFIXES[kind]}_`;
  if (!id.startsWith(prefix)) return null;
  try {
    return ulidToUuid(id.slice(prefix.length));
  } catch {
    // dạng ULID sai: cùng nghĩa với tham chiếu không tồn tại
    return null;
  }
}

export const encodeId = (kind: IdKind, uuid: string): string => `${ID_PREFIXES[kind]}_${uuidToUlid(uuid)}`;

/** UUIDv7: 48 bit ms + 74 bit ngẫu nhiên. `now` / `random` tiêm được để test tất định. */
export function newUuidV7(now: number = Date.now(), random: (n: number) => Uint8Array = (n) => globalThis.crypto.getRandomValues(new Uint8Array(n))): string {
  const b = random(16);
  const t = BigInt(now);
  for (let i = 0; i < 6; i += 1) b[i] = Number((t >> BigInt(8 * (5 - i))) & 0xffn);
  b[6] = (b[6]! & 0x0f) | 0x70;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
